export type RewardShippingInfo = {
  name: string
  phone: string
  address?: string
}

export type RewardRedeemItem = {
  id: string
  name: string
  description?: string
  type: 'credit' | 'item'
  pointsCost: number
  creditAmount?: number
}

export const REWARD_DESCRIPTION_LINE_CLAMP = 5

export function formatRewardPoints(value: number): string {
  return Number(value || 0).toLocaleString('th-TH')
}

export function escapeRewardHtml(value: string): string {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function hasRewardDescriptionDetail(description?: string): boolean {
  return Boolean(String(description || '').trim())
}

export function buildPointsLedgerText(current: number, cost: number): {
  currentLabel: string
  costLabel: string
  remainingLabel: string
  remaining: number
} {
  const remaining = Math.max(0, Number(current || 0) - Number(cost || 0))
  return {
    currentLabel: formatRewardPoints(current),
    costLabel: formatRewardPoints(cost),
    remainingLabel: formatRewardPoints(remaining),
    remaining,
  }
}

export type RewardRedeemFlowHandlers = {
  onUnavailable?: (label: string) => Promise<void>
  onInsufficientPoints: (args: {
    reward: RewardRedeemItem
    currentPoints: number
    requiredPoints: number
  }) => Promise<void>
  onConfirmRedeem: (args: {
    reward: RewardRedeemItem
    currentPoints: number
  }) => Promise<boolean>
  onShippingForm?: () => Promise<RewardShippingInfo | null>
  onFinalSummary: (args: {
    reward: RewardRedeemItem
    currentPoints: number
    requiredPoints: number
    remainingPoints: number
    shippingInfo: RewardShippingInfo | null
  }) => Promise<boolean>
  onSuccess: (args: { message: string; reward: RewardRedeemItem }) => Promise<void>
  onError: (message: string) => Promise<void>
}

export async function runRewardRedeemFlow(options: {
  reward: RewardRedeemItem
  pointsBalance: number
  canRedeem?: boolean
  unavailableLabel?: string
  handlers: RewardRedeemFlowHandlers
  redeem: (shippingInfo: RewardShippingInfo | null) => Promise<{ message?: string }>
}): Promise<void> {
  const { reward, pointsBalance, handlers, redeem } = options
  const requiredPoints = Number(reward.pointsCost || 0)
  const canRedeem = options.canRedeem !== false

  if (!canRedeem) {
    if (handlers.onUnavailable) {
      await handlers.onUnavailable(options.unavailableLabel || 'รางวัลนี้ไม่พร้อมให้แลกในขณะนี้')
    }
    return
  }

  if (pointsBalance < requiredPoints) {
    await handlers.onInsufficientPoints({
      reward,
      currentPoints: pointsBalance,
      requiredPoints,
    })
    return
  }

  const confirmed = await handlers.onConfirmRedeem({
    reward,
    currentPoints: pointsBalance,
  })
  if (!confirmed) return

  let shippingInfo: RewardShippingInfo | null = null
  if (reward.type === 'item' && handlers.onShippingForm) {
    shippingInfo = await handlers.onShippingForm()
    if (!shippingInfo) return
  }

  const { remaining } = buildPointsLedgerText(pointsBalance, requiredPoints)
  const finalConfirmed = await handlers.onFinalSummary({
    reward,
    currentPoints: pointsBalance,
    requiredPoints,
    remainingPoints: remaining,
    shippingInfo,
  })
  if (!finalConfirmed) return

  try {
    const response = await redeem(shippingInfo)
    await handlers.onSuccess({
      message: response.message || 'แลกรางวัลสำเร็จ',
      reward,
    })
  } catch (error) {
    await handlers.onError(error instanceof Error ? error.message : 'เกิดข้อผิดพลาด')
  }
}
